/**
 * HOW MANY SLOT RANKINGS ARE IN — asked once, so the app stops giving two answers.
 *
 * Tim, 31 August: "home says 4 slot rankings are in but order & lottery page says 2. I think the
 * 4 is correct??"
 *
 * Both numbers were true and neither was labelled honestly. Home counted PREFERENCE DOCUMENTS —
 * every list the league holds — and the draft-order strip counted `submittedAt`, which is only
 * set when a manager presses the button. Four lists exist; two were sent.
 *
 * THREE STATES, NOT TWO, because a list that exists and was never sent is neither of the other
 * two and wants its own response:
 *
 *   SENT      he pressed the button. His list decides his slot. Nothing to do.
 *   SAVED     rows exist, no stamp. The resolve does NOT read it — see
 *             `draftOrder.rankingsForResolve` — so at his turn §6 gives him the earliest slot
 *             open, exactly as if he had ranked nothing. He is worth a message before the
 *             deadline, because he is one tap from the outcome he actually wants and almost
 *             certainly believes he has already got it.
 *   NOTHING   no list at all. §6 as well, and no message will change it.
 *
 * `willResolve` is SENT. An earlier version counted `sent + saved` on the reading that
 * `resolveDraftSlots` takes whatever it is handed and the callers were handing it everything —
 * which described the client's preview and never the function that writes the order. The headline
 * a commissioner acts on has to be the number of managers whose own list will be used, and
 * `saved` is the interesting half of what is left rather than part of it.
 */

export interface RankingRow {
  gm: string;
  ranked: readonly number[];
  submittedAt?: string | null;
}

export interface RankingCensus {
  teams: number;
  /** Pressed the button. */
  sent: number;
  /** Saved a list and never sent it. The resolve does NOT read these — §6 takes his turn. */
  saved: number;
  /** No list at all — §6 takes over at his turn. */
  nothing: number;
  /** How many of the ten the resolve will read a ranking for. Sent only. */
  willResolve: number;
  /** Who is in each state, upper-cased, for a screen that wants to name them. */
  sentBy: string[];
  savedBy: string[];
  /** "4 of 10", the headline every screen shows. */
  count: string;
  /** The sub-line under it. Says the split only when there is a split to say. */
  note: string;
}

const up = (s: string) => s.toUpperCase();

export function rankingCensus(
  rows: readonly RankingRow[], teams: number,
): RankingCensus {
  const sentBy: string[] = [];
  const savedBy: string[] = [];
  const seen = new Set<string>();

  for (const r of rows) {
    const gm = up(r.gm);
    /* One row per manager. A duplicate document must not inflate the count past ten. */
    if (seen.has(gm)) continue;
    seen.add(gm);
    if (r.submittedAt) sentBy.push(gm);
    else if (r.ranked.length > 0) savedBy.push(gm);
  }

  const sent = sentBy.length;
  const saved = savedBy.length;
  const willResolve = Math.min(sent, teams);
  const nothing = Math.max(0, teams - willResolve);

  return {
    teams,
    sent,
    saved,
    nothing,
    willResolve,
    sentBy: sentBy.sort(),
    savedBy: savedBy.sort(),
    count: `${willResolve} of ${teams}`,
    /* `saved` is called out of the chase total rather than added to it: those men are the ones
       a message actually moves, and lumping them in with the ones who have written nothing is
       how the earlier version came to describe them as covered. */
    note: saved === 0
      ? (nothing === 0 ? 'all in' : `${nothing} to chase`)
      : `${nothing} to chase · ${saved} saved but never sent, which the resolve will not read`,
  };
}
